import { useState } from 'react'

/*
 * The viewer's chosen columns, kept in this browser. A layout preference rather than
 * part of the view a link reproduces: a link from the Overview carries no column list,
 * and a shared link should open in the reader's own layout. Storage can be missing,
 * full or refused (a private window, blocked site data), so every access is guarded
 * and the defaults stand in.
 */

export interface ColumnChoiceConfig {
  /** This list's own key in `localStorage`. */
  storageKey: string
  /** Every column key, in display order; the first is always shown. */
  keys: readonly string[]
  /** The columns shown until the viewer chooses. */
  defaults: readonly string[]
}

/** The stored choice, if it is one: known keys only, in display order, the first column always included. */
export function parseColumnChoice(raw: string | null, keys: readonly string[]): string[] | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!Array.isArray(parsed)) return null
  const chosen = new Set(parsed.filter((key): key is string => typeof key === 'string'))
  const first = keys[0]
  if (first) chosen.add(first)
  return keys.filter((key) => chosen.has(key))
}

function read({ storageKey, keys, defaults }: ColumnChoiceConfig): string[] {
  try {
    return parseColumnChoice(window.localStorage.getItem(storageKey), keys) ?? [...defaults]
  } catch {
    return [...defaults]
  }
}

function write(storageKey: string, keys: readonly string[] | null) {
  try {
    if (keys === null) window.localStorage.removeItem(storageKey)
    else window.localStorage.setItem(storageKey, JSON.stringify(keys))
  } catch {
    // Not kept: the choice still holds for this visit.
  }
}

export function useColumnChoice(config: ColumnChoiceConfig): {
  chosen: readonly string[]
  toggle: (key: string) => void
  reset: () => void
} {
  const [chosen, setChosen] = useState<string[]>(() => read(config))
  const { storageKey, keys, defaults } = config

  const choose = (next: string[] | null) => {
    write(storageKey, next)
    setChosen(next ?? [...defaults])
  }

  return {
    chosen,
    toggle: (key) => {
      if (key === keys[0]) return
      choose(
        chosen.includes(key)
          ? chosen.filter((candidate) => candidate !== key)
          : keys.filter((candidate) => candidate === key || chosen.includes(candidate)),
      )
    },
    reset: () => choose(null),
  }
}
